import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Wallet } from '../services/medewerkerApi';

const eur = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/**
 * Payroll Bridge: twee fiscaal gescheiden potten van de medewerker.
 * Pot 1 (professioneel, gerichte vrijstelling) en pot 2 (persoonlijk/hobby,
 * WKR vrije ruimte). Saldo lekt nooit van de ene naar de andere pot.
 */
export const WalletPotten: React.FC<{ wallet: Wallet }> = ({ wallet }) => {
  const { t } = useTranslation('medewerker');
  const p = wallet.potten;
  if (!p) return null;
  if (p.status !== 'ACTIVE') {
    return <p className="text-sm text-gray-600">{t('academy.potInactief', { defaultValue: 'Je budget is niet meer actief.' })}</p>;
  }
  const Pot: React.FC<{ naam: string; sub: string; totaal: number; gebruikt: number; resterend: number; kleur: string }> = ({ naam, sub, totaal, gebruikt, resterend, kleur }) => {
    const pct = totaal > 0 ? Math.min(100, Math.round((gebruikt / totaal) * 100)) : 0;
    return (
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-medium text-gray-900">{naam}</p>
          <p className="text-sm font-semibold text-green-800">{eur.format(resterend)}</p>
        </div>
        <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={naam}>
          <div className={`h-full rounded-full ${kleur}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {t('academy.gebruiktVan', { defaultValue: '{{gebruikt}} van {{totaal}} gebruikt', gebruikt: eur.format(gebruikt), totaal: eur.format(totaal) })} · {sub}
        </p>
      </div>
    );
  };
  return (
    <div className="space-y-3">
      <Pot
        naam={t('academy.potPro', { defaultValue: 'Professioneel budget' })}
        sub={t('academy.potProSub', { defaultValue: 'mentaal, fysiek & professionele groei · belastingvrij' })}
        totaal={p.pro.totaal}
        gebruikt={p.pro.gebruikt}
        resterend={p.pro.resterend}
        kleur="bg-green-500"
      />
      <Pot
        naam={t('academy.potHobby', { defaultValue: 'Persoonlijk / hobby budget' })}
        sub={t('academy.potHobbySub', { defaultValue: 'passie & hobby’s · WKR vrije ruimte' })}
        totaal={p.hobby.totaal}
        gebruikt={p.hobby.gebruikt}
        resterend={p.hobby.resterend}
        kleur="bg-teal-500"
      />
    </div>
  );
};

export default WalletPotten;
